import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Typography,
  Tag,
  Space,
  Button,
  Alert,
  theme,
} from "antd";
import {
  TeamOutlined,
  AppstoreOutlined,
  RightOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import PageLayout from "@components/pageLayout/PageLayout.react";
import { authenticatedFetch } from "@/libs/api";
import { makePagination } from "@components/common/TablePagination";

const { Title, Text } = Typography;

function formatPower(val, unit) {
  if (val >= 1000) {
    return { value: (val / 1000).toFixed(1), suffix: unit === "kW" ? "MW" : "MWh" };
  }
  return { value: val.toFixed(2), suffix: unit };
}

export default function GroupSummary() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [onboardedGroupLoading, setOnboardedGroupLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [onboardedGroup, setOnboardedGroup] = useState(null);
  const [userDefaultViewGroupId, setUserDefaultViewGroupId] = useState(null);
  const [apiErrors, setApiErrors] = useState([]);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    // Fetch the company's default view group
    const fetchOnboardedGroup = async () => {
      setOnboardedGroupLoading(true);
      try {
        const response = await authenticatedFetch("/api/groups/onboarded");
        const result = await response.json();

        if (result.success && result.data) {
          setOnboardedGroup(result.data);
          // Collect any errors from the response
          if (result.errors && result.errors.length > 0) {
            setApiErrors(prev => [...prev, ...result.errors.map(e => ({
              ...e,
              endpoint: '/api/groups/onboarded',
            }))]);
          }
        } else {
          setApiErrors(prev => [...prev, {
            source: 'API',
            endpoint: '/api/groups/onboarded',
            message: result.message || result.error || 'Failed to fetch onboarded group',
          }]);
        }
      } catch (error) {
        console.error("Failed to fetch onboarded group:", error);
        setApiErrors(prev => [...prev, {
          source: 'Network',
          endpoint: '/api/groups/onboarded',
          message: error.message || 'Network error',
        }]);
      } finally {
        setOnboardedGroupLoading(false);
      }
    };

    // Fetch groups data
    const fetchGroups = async () => {
      setLoading(true);
      try {
        const response = await authenticatedFetch("/api/groups");
        const result = await response.json();

        if (result.success && result.data) {
          setGroups(result.data);
          // Store user's default view group ID for filtering
          if (result.userDefaultViewGroupId) {
            setUserDefaultViewGroupId(result.userDefaultViewGroupId);
          }
          // Collect any errors from the response
          if (result.errors && result.errors.length > 0) {
            setApiErrors(prev => [...prev, ...result.errors.map(e => ({
              ...e,
              endpoint: '/api/groups',
            }))]);
          }
        } else {
          setApiErrors(prev => [...prev, {
            source: 'API',
            endpoint: '/api/groups',
            message: result.message || result.error || 'Failed to fetch groups',
          }]);
        }
      } catch (error) {
        console.error("Failed to fetch groups:", error);
        setApiErrors(prev => [...prev, {
          source: 'Network',
          endpoint: '/api/groups',
          message: error.message || 'Network error',
        }]);
      } finally {
        setLoading(false);
      }
    };

    // Clear previous errors before fetching
    setApiErrors([]);
    fetchOnboardedGroup();
    fetchGroups();
  }, []);

  const handleViewGroup = (groupId) => {
    navigate(`/group-management/groups/${groupId}`);
  };

  // Format error message for display
  const formatErrorMessage = (error) => {
    const parts = [];
    if (error.source) parts.push(`[${error.source}]`);
    if (error.table) parts.push(`Table: ${error.table}`);
    if (error.column) parts.push(`Column: ${error.column}`);
    if (error.code) parts.push(`Code: ${error.code}`);
    if (error.endpoint) parts.push(`Endpoint: ${error.endpoint}`);
    if (error.groupId) parts.push(`Group: ${error.groupId}`);
    parts.push(error.message);
    return parts.join(' | ');
  };

  // Get group type display info
  const getGroupTypeTag = (groupType) => {
    switch (groupType) {
      case 'default':
        return { label: 'Default', color: 'purple' };
      case 'view':
        return { label: 'View', color: 'blue' };
      case 'control':
        return { label: 'Control', color: 'orange' };
      default:
        return { label: 'View', color: 'blue' };
    }
  };

  // Common columns for both tables (same format)
  const columns = [
    {
      title: "Group Name",
      dataIndex: "name",
      key: "name",
      render: (name, record) => {
        const typeTag = getGroupTypeTag(record.groupType);
        return (
          <div>
            <Text strong style={{ fontSize: 14 }}>{name}</Text>
            <div>
              <Tag color={typeTag.color} style={{ fontSize: 10, marginTop: 4 }}>{typeTag.label}</Tag>
            </div>
          </div>
        );
      },
    },
    {
      title: "Systems",
      dataIndex: "customers",
      key: "customers",
      align: "center",
      sorter: (a, b) => (a.customers || 0) - (b.customers || 0),
      render: (val) => (
        <Text strong style={{ fontSize: 16 }}>{val || 0}</Text>
      ),
    },
    {
      title: "Inverter Capacity",
      dataIndex: "inverterKW",
      key: "inverterKW",
      align: "right",
      sorter: (a, b) => (a.inverterKW || 0) - (b.inverterKW || 0),
      render: (val) => {
        const f = formatPower(val || 0, "kW");
        return (
        <div>
          <Text strong style={{ color: token.colorPrimary }}>{f.value}</Text>
          <Text type="secondary" style={{ marginLeft: 4, fontSize: 12 }}>{f.suffix}</Text>
        </div>
        );
      },
    },
    {
      title: "Battery Capacity",
      dataIndex: "batteryKWh",
      key: "batteryKWh",
      align: "right",
      sorter: (a, b) => (a.batteryKWh || 0) - (b.batteryKWh || 0),
      render: (val) => {
        const f = formatPower(val || 0, "kWh");
        return (
        <div>
          <Text strong style={{ color: token.colorSuccess }}>{f.value}</Text>
          <Text type="secondary" style={{ marginLeft: 4, fontSize: 12 }}>{f.suffix}</Text>
        </div>
        );
      },
    },
    {
      // POW-166: PV generated over the last 30 days. The API already computed
      // this (SUM(total_epv)) but nothing displayed it.
      title: "Generation",
      dataIndex: "totalGenerationKWh",
      key: "totalGenerationKWh",
      align: "right",
      sorter: (a, b) => (a.totalGenerationKWh || 0) - (b.totalGenerationKWh || 0),
      render: (val) => (
        <div>
          <Text strong>{(val || 0).toLocaleString()}</Text>
          <Text type="secondary" style={{ marginLeft: 4, fontSize: 12 }}>kWh</Text>
        </div>
      ),
    },
    {
      // POW-166: this column was headed "Total Discharge" but the value behind
      // it is SUM(total_esell) — energy EXPORTED TO THE GRID, not battery
      // discharge. Measured on a real group: 12,082 kWh export vs 11,599 kWh
      // actual battery discharge over the same 30 days.
      //
      // The API now names it `totalGridExportKWh`; `totalDischargeKWh` survives
      // on /api/groups only as a deprecated alias for the components that still
      // read it (CompanySnapshot, SettlementRuleManagement, SystemDetail).
      //
      // Real battery discharge is available per-group on the Detail page, which
      // resolves it from Cassandra. It is deliberately NOT fetched here: this is
      // a list, and it would cost N groups x 30 days of partition reads.
      title: "Grid Export",
      dataIndex: "totalGridExportKWh",
      key: "totalGridExportKWh",
      align: "right",
      sorter: (a, b) => (a.totalGridExportKWh || 0) - (b.totalGridExportKWh || 0),
      render: (val) => (
        <div>
          <Text strong>{(val || 0).toLocaleString()}</Text>
          <Text type="secondary" style={{ marginLeft: 4, fontSize: 12 }}>kWh</Text>
        </div>
      ),
    },
    {
      title: "Revenue",
      dataIndex: "revenue",
      key: "revenue",
      align: "right",
      sorter: (a, b) => (a.revenue || 0) - (b.revenue || 0),
      render: (val) => (
        <Text strong style={{ color: "#faad14", fontSize: 15 }}>
          ${(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      filters: [
        { text: "Active", value: "active" },
        { text: "Inactive", value: "inactive" },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status) => (
        <Tag color={status === "active" ? "success" : "default"}>
          {status === "active" ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          icon={<RightOutlined />}
          onClick={() => handleViewGroup(record.id)}
          style={{ padding: 0 }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <PageLayout active="groupSummary">
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ marginBottom: 4 }}>Group Summary</Title>
        <Text type="secondary" style={{ fontSize: 14 }}>
          Overview of all VPP groups and their performance metrics
        </Text>
      </div>

      {/* Error Alert Banner */}
      {apiErrors.length > 0 && (
        <Alert
          type="warning"
          icon={<WarningOutlined />}
          showIcon
          closable
          style={{ marginBottom: 24 }}
          message="Database Query Warnings"
          description={
            <div style={{ maxHeight: 200, overflowY: 'auto' }}>
              {apiErrors.map((error, index) => (
                <div key={index} style={{
                  fontFamily: 'monospace',
                  fontSize: 12,
                  padding: '4px 0',
                  borderBottom: index < apiErrors.length - 1 ? '1px solid #ffd591' : 'none'
                }}>
                  {formatErrorMessage(error)}
                </div>
              ))}
            </div>
          }
        />
      )}

      {/* Default View Group (company's default) */}
      <Card
        bordered={false}
        style={{ borderRadius: 12, marginBottom: 24 }}
        title={
          <Space>
            <AppstoreOutlined style={{ color: token.colorPrimary }} />
            <span style={{ fontSize: 16, fontWeight: 600 }}>
              {onboardedGroup?.name || "Default View Group"}
            </span>
          </Space>
        }
        extra={
          <Text type="secondary" style={{ fontSize: 13 }}>
            Default view group for this company
          </Text>
        }
        styles={{
          header: {
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            padding: "12px 16px",
            minHeight: 48,
          },
          body: { padding: 0 },
        }}
      >
        <Table
          columns={columns}
          dataSource={onboardedGroup ? [onboardedGroup] : []}
          rowKey="id"
          loading={onboardedGroupLoading}
          pagination={false}
          style={{ cursor: "pointer" }}
          onRow={(record) => ({
            onClick: () => handleViewGroup(record.id),
            style: { cursor: "pointer" },
          })}
        />
      </Card>

      {/* Other Groups Table (excluding default view group) */}
      <Card
        bordered={false}
        style={{ borderRadius: 12 }}
        title={
          <Space>
            <TeamOutlined style={{ color: token.colorPrimary }} />
            <span style={{ fontSize: 16, fontWeight: 600 }}>Other Groups</span>
          </Space>
        }
        extra={
          <Text type="secondary" style={{ fontSize: 13 }}>
            {groups.filter(g => g.id !== userDefaultViewGroupId && g.id !== onboardedGroup?.id).length} groups total
          </Text>
        }
        styles={{
          header: {
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            padding: "12px 16px",
            minHeight: 48,
          },
          body: { padding: 0 },
        }}
      >
        <Table
          columns={columns}
          dataSource={groups.filter(g => g.id !== userDefaultViewGroupId && g.id !== onboardedGroup?.id)}
          rowKey="id"
          loading={loading}
          pagination={makePagination(pageSize, setPageSize)}
          style={{ cursor: "pointer" }}
          onRow={(record) => ({
            onClick: () => handleViewGroup(record.id),
            style: { cursor: "pointer" },
          })}
        />
      </Card>
    </PageLayout>
  );
}
